import { createDirectStore, } from 'direct-vuex';
import Vue from 'vue';
import Vuex from 'vuex';
import { BankDetailsInput, } from '~/api/self-serve-client';
import { formatSortCode, } from '~/utils/bankDetailsFormattingMethods';
import { AddressData, IMovingDetails, isAddressData, } from '~/utils/models';
import { isDate, isObject, } from '~/utils/models/isObject';
import { initialiseStoreValue, } from '~/utils/store';
import alertModule from '~/utils/store/alert-module';
import globalConfigModule from '~/utils/store/global-config-module';

Vue.use(Vuex);

const UserDetailsStorageKey = 'userDetails';
const CurrentMeterReadStorageKey = 'moveWithin.currentReading';
const MovingDetailsStorageKey = 'moveWithin.movingDetails';
const PaymentDetailsStorageKey = 'moveWithin.paymentDetails';
const MoveOutDateStorageKey = 'moveWithin.moveOutDate';
const CustomerMoveOutDetailsStorageKey = 'moveWithin.moveOutDetails';
const CompletedMoveDetailsStorageKey = 'moveWithin.completedMoveDetails';

interface UserDetails {
	customerFirstName?: string;
	customerLastName: string;
	customerEmail: string;
	customerNumber: number;
	customerRefNumber: string;
}

export interface DirectDebitPayments {
	isDirectDebit: true;
	paymentDate: number | null;
	paymentFrequency: string;
	ddPaymentAmount: number | null;
}

export interface BilledPayments {
	isDirectDebit: false;
}
export type PaymentDetails = DirectDebitPayments | BilledPayments;

export interface MoveOutDetails {
	id: string;
	category: string;
	lastReading: number;
	lastReadDate: Date | string;
}

export interface CustomerMoveOutDetails {
	contractId: string;
	propertyAddress: AddressData;
	meter: MoveOutDetails;
}

export interface CompletedMoveDetails {
	paymentReferenceNumber: string;
	customerServiceFlag: string;
	isMoveWithinForcefullySucceeded: boolean;
}

export interface OverlayData {
	navigateTo: string;
	message: string;
}

export type RecommendedAmountThreshold = {
	recommendedAmount: number | null,
	minAmount: number | null,
	maxAmount: number | null,
};

export interface MoveWithinState {
	isOverlayVisible: boolean;
	overlayMessage: string;
	navigateTo: string;
	userDetails: UserDetails | null;
	moveOutDetails: CustomerMoveOutDetails | undefined;
	currentStep: 1 | 2 | 3 | 4 | 5 | 6 | null;
	showSpinner: boolean;
	movingDetails: IMovingDetails;
	editMode: boolean;
	addressList: AddressData[];
	addressListC: AddressData[];
	lastStep: number;
	bankDetails: BankDetailsInput | null;
	paymentDetails: PaymentDetails;
	moveOutDate: Date | null;
	customerMeterType: string;
	currentMeterReading: number | null;
	completedMoveDetails: CompletedMoveDetails | null;
	directDebitSetupFailureReason: 'validation' | 'paymentscheme' | 'other' | null;
	isManualAddressEntryForSupplyAddress: boolean;
	recommendedAmountThreshold: RecommendedAmountThreshold;
	isOverlayWithSlotVisible: boolean;
	paymentDateInApiDateFormat: string | null;
}

function isUserDetails(input: unknown): input is UserDetails {
	return (
		true &&
		isObject(input) &&
		typeof input.customerLastName === 'string' &&
		typeof input.customerEmail === 'string' &&
		typeof input.customerNumber === 'number' &&
		typeof input.customerRefNumber === 'string' &&
		true
	);
}

function initialiseMovingDetails() {
	return initialiseStoreValue(MovingDetailsStorageKey, isMovingDetails, (): IMovingDetails => ({
		newAddress: null,
		moveInDate: null,
		isCorrespondenceAddress: true,
		correspondenceAddress: null,
		occupancyType: 'Owner' as const,
		noPersons: null,
		personsToDisplayText: null,
	}), (parsed) => {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		parsed.moveInDate = parsed.moveInDate ? new Date(parsed.moveInDate) : null;
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return parsed;
	});
}

function isMovingDetails(input: unknown): input is IMovingDetails {
	return (
		true &&
		isObject(input) &&
		isObject(input.newAddress) &&
		isDate(input.moveInDate) &&
		typeof input.isCorrespondenceAddress === 'boolean' &&
		(input.isCorrespondenceAddress === true || isObject(input.correspondenceAddress)) &&
		typeof input.occupancyType === 'string' &&
		typeof input.noPersons === 'number' &&
		true
	);
}
function initialisePaymentDetails(): PaymentDetails {
	return initialiseStoreValue(PaymentDetailsStorageKey, isPaymentDetails, () => ({
		isDirectDebit: true,
		paymentDate: null,
		paymentFrequency: 'M',
		ddPaymentAmount: null,
	}));
}

function isPaymentDetails(input: unknown): input is PaymentDetails {
	return isBilledPayments(input) || isDirectDebitPayments(input);
}

function isBilledPayments(input: unknown): input is BilledPayments {
	return isObject(input)
		&& input.isDirectDebit === false;
}

function isDirectDebitPayments(input: unknown): input is DirectDebitPayments {
	return isObject(input)
		&& input.isDirectDebit === true
		&& typeof input.paymentDate === 'number'
		&& typeof input.paymentFrequency === 'string';
}

function initialiseMoveOutDate() : Date | null {
	return initialiseStoreValue(MoveOutDateStorageKey, isDate, () => null, (v) => new Date(v));
}

function isMeterDetails(input: unknown): input is MoveOutDetails {
	return (
		true &&
		isObject(input) &&
		typeof input.id === 'string' &&
		typeof input.category === 'string' &&
		typeof input.lastReading === 'number' &&
		isDate(input.lastReadDate) &&
		true
	);
}

function isCustomerMoveOutDetails(input: unknown): input is CustomerMoveOutDetails {
	return isObject(input) &&
		typeof input.contractId === 'string' &&
		isAddressData(input.propertyAddress) &&
		isMeterDetails(input.meter);
}

function initialiseCustomerMoveOutDetails() {
	return initialiseStoreValue(CustomerMoveOutDetailsStorageKey,
		isCustomerMoveOutDetails,
		() => undefined,
		(userInput) => {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			userInput.meter.lastReadDate = new Date(userInput.meter.lastReadDate);
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			return userInput;
		});
}

function initialiseUserDetails(): UserDetails | null {
	return initialiseStoreValue(UserDetailsStorageKey, isUserDetails, () => null);
}

function initialiseCurrentReading() : number | null {
	return initialiseStoreValue(CurrentMeterReadStorageKey, (v): v is number => typeof v === 'string' && !isNaN(parseInt(v, 10)), () => null);
}

function initialiseCompletedMoveDetails() {
	return initialiseStoreValue(
		CompletedMoveDetailsStorageKey,
		(input): input is CompletedMoveDetails => {
			return isObject(input) &&
			typeof input.paymentReferenceNumber === 'string' &&
			typeof input.customerServiceFlag === 'string';
		},
		() => null);
}

const { store, } = createDirectStore({
	state: {
		currentStep: 1,
		movingDetails: initialiseMovingDetails(),
		showSpinner: false,
		userDetails: initialiseUserDetails(),
		editMode: false,
		addressList: [] as AddressData[],
		addressListC: [] as AddressData[],
		lastStep: null as number | null,
		bankDetails: null as BankDetailsInput | null,
		paymentDetails: initialisePaymentDetails(),
		moveOutDate: initialiseMoveOutDate(),
		moveOutDetails: initialiseCustomerMoveOutDetails(),
		customerMeterType: null as (string | null),
		currentMeterReading: initialiseCurrentReading(),
		completedMoveDetails: initialiseCompletedMoveDetails(),
		directDebitSetupFailureReason: null,
		isManualAddressEntryForSupplyAddress: false,
		overlayMessage: '',
		navigateTo: '',
		isOverlayVisible: false,
		paymentDateInApiDateFormat: null,
		recommendedAmountThreshold: {
			recommendedAmount: null,
			minAmount: null,
			maxAmount: null,
		},
		isOverlayWithSlotVisible: false,
	} as MoveWithinState,
	getters: {
		hasExistingUserDetails(state) {
			return state.userDetails !== null;
		},
		hasExistingMoveOutDetails(state) {
			return state.moveOutDetails !== undefined;
		},
		hasExistingMoveingDetails(state) {
			return state.movingDetails !== undefined;
		},
		hasCompletedMoveIn(state) {
			return state.completedMoveDetails != null;
		},
		hasExistingPaymentDetails(state) {
			return state.bankDetails != null;
		},
		selectedPaymentMethod(state) {
			switch (state.paymentDetails.isDirectDebit) {
				case true:
					return 'Pay by Direct Debit';
				case false:
				default:
					return 'Pay on receipt of bill';
			}
		},
		formattedSortCode(state) {
			const sortCode = state.bankDetails?.sortCode;
			if (!sortCode) {
				return '';
			}
			return formatSortCode(sortCode);
		},
	},
	mutations: {
		showSpinner(state) {
			state.showSpinner = true;
		},
		hideSpinner(state) {
			state.showSpinner = false;
		},
		setStep(state, step: MoveWithinState['currentStep']) {
			state.currentStep = step;
		},
		updateUserDetails(state, userDetails: UserDetails) {
			sessionStorage.setItem(UserDetailsStorageKey, JSON.stringify(userDetails));
			state.userDetails = userDetails;
		},
		setMeterReading(state, meterReading: number | null) {
			if (typeof meterReading !== 'number') {
				sessionStorage.setItem(CurrentMeterReadStorageKey, 'null');
			} else {
				sessionStorage.setItem(CurrentMeterReadStorageKey, meterReading.toString(10));
			}
			state.currentMeterReading = meterReading;
		},
		updateMeterDetails(state, meterDetails: CustomerMoveOutDetails) {
			sessionStorage.setItem(CustomerMoveOutDetailsStorageKey, JSON.stringify(meterDetails));
			state.moveOutDetails = meterDetails;
		},
		checkCustomerMeterType(state, customerMeterType: string) {
			state.customerMeterType = customerMeterType;
		},
		setDirectDebitPayments(state, { bankDetails, paymentDetails, }: {
			paymentDetails: Omit<DirectDebitPayments, 'isDirectDebit'>,
			bankDetails: BankDetailsInput,
		}) {
			const storeDetails = {
				isDirectDebit: true as const,
				...paymentDetails,
			};
			sessionStorage.setItem(PaymentDetailsStorageKey, JSON.stringify(storeDetails));
			state.paymentDetails = storeDetails;
			state.bankDetails = bankDetails;
		},
		closeOverlay(state) {
			state.isOverlayVisible = false;
		},
		showOverlay(state, data: OverlayData) {
			state.isOverlayVisible = true;
			state.overlayMessage = data.message;
			state.navigateTo = data.navigateTo;
		},
		showOverlayWithSlot(state) {
			state.isOverlayWithSlotVisible = true;
		},
		closeOverlayWithSlot(state) {
			state.isOverlayWithSlotVisible = false;
		},
		setRecommendedAmountThreshold(state, details:RecommendedAmountThreshold) {
			state.recommendedAmountThreshold = { ...details, };
		},
		setPaymentDateIntoApiDateFormat(state, date:string) {
			state.paymentDateInApiDateFormat = date;
		},
		reset(state) {
			state.currentStep = null;
			sessionStorage.removeItem(MovingDetailsStorageKey);
			sessionStorage.removeItem(PaymentDetailsStorageKey);
			sessionStorage.removeItem(MoveOutDateStorageKey);
			sessionStorage.removeItem(CustomerMoveOutDetailsStorageKey);
			state.movingDetails = initialiseMovingDetails();
			state.moveOutDate = initialiseMoveOutDate();
			state.moveOutDetails = initialiseCustomerMoveOutDetails();
			state.editMode = false;
			state.addressList = [];
			state.addressListC = [];
			state.lastStep = 0;
			state.paymentDetails = initialisePaymentDetails();
			state.bankDetails = null;
			state.directDebitSetupFailureReason = null;
			state.userDetails = initialiseUserDetails();
			state.isManualAddressEntryForSupplyAddress = false;
			state.paymentDateInApiDateFormat = null;
			state.recommendedAmountThreshold = {
				recommendedAmount: null,
				minAmount: null,
				maxAmount: null,
			};
		},
		updateMovingDetails(state, movingDetails: IMovingDetails) {
			sessionStorage.setItem(
				MovingDetailsStorageKey,
				JSON.stringify(movingDetails)
			);
			state.movingDetails = movingDetails;
		},
		updateEditMode(state, status: boolean) {
			state.editMode = status;
		},
		setAddressList(state, list: AddressData[]) {
			state.addressList = list;
		},
		setAddressListC(state, list: AddressData[]) {
			state.addressListC = list;
		},
		setManualAddressEntryForSupplyAddress(state, status: boolean) {
			state.isManualAddressEntryForSupplyAddress = status;
		},
		setBilledPayments(state) {
			const paymentDetails: BilledPayments = {
				isDirectDebit: false,
			};
			sessionStorage.setItem(PaymentDetailsStorageKey, JSON.stringify(paymentDetails));
			state.paymentDetails = paymentDetails;
			state.bankDetails = null;
		},
		updateMoveOutDate(state, moveOutDate: Date) {
			state.moveOutDate = moveOutDate;
			sessionStorage.setItem(MoveOutDateStorageKey, JSON.stringify(state.moveOutDate));
		},
		setLastStep(state, step: number) {
			state.lastStep = step;
		},
		setCompletedDetails(state, { paymentReferenceNumber, customerServiceFlag, isMoveWithinForcefullySucceeded, }: CompletedMoveDetails) {
			state.completedMoveDetails = { paymentReferenceNumber, customerServiceFlag, isMoveWithinForcefullySucceeded, };
		},
		setDDSetupFailureReason(state, arg: MoveWithinState['directDebitSetupFailureReason']) {
			state.directDebitSetupFailureReason = arg;
		},
	},
	modules: {
		alertModule,
		globalConfigModule,
	},
});

export const Store = store;
